import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Blocker, Button } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { routes } from '~/providers/RouterProvider/router.routes';
import { isNotBlank } from '~/utils/string';

import type { EuropeanOrganisationWithOtherNumberFormType, CreateEuropeanOrganisationWithOtherNumberProps as Props } from './types';

import { FormAddressCity } from '../../components/FormAddressCity';
import { FormAddressNumber } from '../../components/FormAddressNumber';
import { FormAddressStreet } from '../../components/FormAddressStreet';
import { FormAddressZipCode } from '../../components/FormAddressZipCode';
import { FormCompanyName } from '../../components/FormCompanyName';
import { mapVatCountryCodeToCountryCode } from '../../utils';
import { useCreateClientContext } from '../context';
import { WizardFooter } from '../WizardFooter/WizardFooter';
import { SwitchNumberType } from './components/SwitchNumberType';
import { VatCountryCodeSelect } from './components/VatCountryCodeSelect';
import { useCreateOrganisationContext } from './context';
import { useIsBlocked } from './hooks';
import { fullWidthStyle, gridStyle, searchRowStyle, stepStyle } from './organisationStep.css';

export const CreateEuropeanOrganisationWithOtherNumber = ({ switchToVatNumber }: Props) => {
  const { organisation, setOrganisation } = useCreateClientContext();
  const { vatCountryCode } = useCreateOrganisationContext();

  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation(['clients', 'validation']);

  const { control, handleSubmit } = useForm<EuropeanOrganisationWithOtherNumberFormType>({
    defaultValues: {
      otherNumber: organisation?.otherNumber ?? '',
      companyName: organisation?.companyName ?? '',
      address: {
        street: organisation?.address.street ?? '',
        number: organisation?.address.number ?? '',
        zipCode: organisation?.address.zipCode ?? '',
        city: organisation?.address.city ?? '',
        countryCode: organisation?.address.countryCode ?? mapVatCountryCodeToCountryCode(vatCountryCode),
      },
    },
  });

  const isBlocked = useIsBlocked(control);

  const onSubmit = handleSubmit((data) => {
    setOrganisation({ clientType: 'organisation', ...data });
    navigate({ pathname: routes.createClientContactStep, search }, { state: bypassBlocker });
  });

  return (
    <>
      <Blocker isBlocked={isBlocked} message={t('clients:create.blocker')} />

      <Form autoComplete="off" className={stepStyle} onSubmit={onSubmit}>
        <div className={searchRowStyle}>
          <VatCountryCodeSelect />

          <Controller
            control={control}
            name="otherNumber"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('clients:fields.otherNumber.label')}</Field.Label>

                <Input autoFocus onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>

                <SwitchNumberType i18nKey="clients:create.company.hasVatNumberLink" onSwitch={switchToVatNumber} />
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
            }}
          />
        </div>

        <div className={gridStyle}>
          <FormCompanyName className={fullWidthStyle} control={control} />
          <FormAddressStreet control={control} />
          <FormAddressNumber control={control} />
          <FormAddressZipCode control={control} />
          <FormAddressCity control={control} />
        </div>

        <WizardFooter backTo={{ pathname: routes.createClient, search }}>
          <Button icon="ArrowRight" iconPlacement="right" type="submit">
            {t('clients:create.next')}
          </Button>
        </WizardFooter>
      </Form>
    </>
  );
};
